import { editorialFunctions } from "@/lib/editorial-team"
import type { EditorialFunction, PipelineStageEstimate, PipelineStageTemplate, PipelineTemplate } from "@/types/domain"

export const maxStageEstimateDays = 999

/** Estimativas são ligadas ao nome da etapa, sem diferenciar maiúsculas nem espaços nas pontas. */
export function stageEstimateKey(stageName: string) {
  return stageName.trim().toLocaleLowerCase("pt-BR")
}

export function estimateCellKey(imprintId: string, stageName: string) {
  return `${imprintId}::${stageEstimateKey(stageName)}`
}

/** Versão cujas etapas viram colunas: a publicada mais recente ou, sem publicação, a mais recente. */
export function estimateStagesVersion(template: PipelineTemplate) {
  const versions = [...template.versions].sort((left, right) => right.version - left.version)
  return versions.find((version) => version.publishedAt) ?? versions[0]
}

/** Converte o texto digitado (aceita vírgula decimal) em dias; vazio é `null` e inválido é `undefined`. */
export function parseEstimateDays(value: string): number | null | undefined {
  const normalized = value.trim().replace(",", ".")
  if (!normalized) return null
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return undefined
  const days = Number(normalized)
  return days > 0 && days <= maxStageEstimateDays ? days : undefined
}

export function formatEstimateDays(days: number) {
  return days.toLocaleString("pt-BR", { maximumFractionDigits: 2 })
}

/** Quantidade com a unidade: “1 dia”, “2,5 dias”. */
export function formatEstimateDaysLabel(days: number) {
  return `${formatEstimateDays(days)} ${days === 1 ? "dia" : "dias"}`
}

export function estimatesToDraft(estimates: PipelineStageEstimate[], stages: PipelineStageTemplate[]) {
  const stageKeys = new Set(stages.map((stage) => stageEstimateKey(stage.name)))
  const draft: Record<string, string> = {}
  for (const estimate of estimates) {
    if (stageKeys.has(stageEstimateKey(estimate.stageName))) draft[estimateCellKey(estimate.imprintId, estimate.stageName)] = formatEstimateDays(estimate.days)
  }
  return draft
}

/** Dias de um clube somadas pela função editorial de cada etapa; etapas sem função ficam com `null`, no fim. */
export function estimateDaysByFunction(stages: PipelineStageTemplate[], draft: Record<string, string>, imprintId: string) {
  const totals = new Map<EditorialFunction | null, number>()
  for (const stage of stages) {
    const days = parseEstimateDays(draft[estimateCellKey(imprintId, stage.name)] ?? "")
    if (!days) continue
    const editorialFunction = stage.editorialFunction ?? null
    totals.set(editorialFunction, (totals.get(editorialFunction) ?? 0) + days)
  }
  return [...editorialFunctions, null].flatMap((editorialFunction) => {
    const days = totals.get(editorialFunction)
    return days ? [{ editorialFunction, days }] : []
  })
}
